import type { components } from '@connected/api-client/api.generated';

import { Tooltip, TooltipContent, TooltipTrigger } from '@connected/ui/components/tooltip';
import { cn } from '@connected/ui/lib/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ScopeSeverity } from '../utils/observation-severity-utils';
import type { InspectionKinds } from '../utils/observation-types';

import {
    getActiveScopeSeverities,
    getHighestSeverity,
    getSeverityColor,
    sortBySeverityPriority,
} from '../utils/observation-severity-utils';

export type SeverityIndicatorVariant = 'single' | 'multiple' | 'stacked' | 'labeled';

export type ObservationSeverityIndicatorProps = {
    /** Array of observation reports containing per-scope severity information */
    reports?: components['schemas']['ListElectricalProjectObservationQueryResultReport'][] | null;
    /** Fallback code kind to use when reports are empty or unavailable */
    fallbackCodeKind?: components['schemas']['ElectricalObservationClassificationCodeKind'] | null;
    /** Visual variant for displaying the severity indicator */
    variant?: SeverityIndicatorVariant;
    /** Additional CSS classes */
    className?: string;
};

/**
 * Displays observation severity indicator(s) with support for multiple display variants.
 * Can show single or multiple scope severities depending on the variant prop.
 */
export function ObservationSeverityIndicator({
    reports,
    fallbackCodeKind,
    variant = 'single',
    className,
}: ObservationSeverityIndicatorProps) {
    const severities = getActiveScopeSeverities(reports);

    // If no active severities, try to use fallback
    if (severities.length === 0) {
        if (!fallbackCodeKind) {
            return null;
        }
        // Render single dot with fallback color
        return (
            <SingleIndicator
                color={getSeverityColor(fallbackCodeKind)}
                className={className}
            />
        );
    }

    switch (variant) {
        case 'multiple':
            return <MultipleIndicator severities={severities} className={className} />;
        case 'stacked':
            return <StackedIndicator severities={severities} className={className} />;
        case 'labeled':
            return <LabeledIndicator severities={severities} className={className} />;
        case 'single':
        default: {
            const highest = getHighestSeverity(severities);
            return (
                <SingleIndicator
                    color={highest?.hexColor ?? getSeverityColor(fallbackCodeKind)}
                    className={className}
                />
            );
        }
    }
}

// ============================================================================
// Variant Components
// ============================================================================

type SingleIndicatorProps = {
    color: string;
    className?: string;
};

/**
 * Single colored dot indicator (current/default behavior).
 * Shows only the highest severity.
 */
function SingleIndicator({ color, className }: SingleIndicatorProps) {
    return (
        <div
            className={cn('h-5 w-5 rounded-full', className)}
            style={{ backgroundColor: color }}
        />
    );
}

type MultiSeverityIndicatorProps = {
    severities: ScopeSeverity[];
    className?: string;
};

/**
 * Multiple dots displayed horizontally.
 * Shows one dot per active scope.
 */
export function MultipleIndicator({ severities, className }: MultiSeverityIndicatorProps) {
    const sortedSeverities = sortBySeverityPriority(severities);

    return (
        <div className={cn('flex items-center gap-1', className)}>
            {sortedSeverities.map(severity => (
                <div
                    key={severity.inspectionKind}
                    className="h-4 w-4 rounded-full"
                    style={{ backgroundColor: severity.hexColor }}
                />
            ))}
        </div>
    );
}

/**
 * Stacked indicator showing overlapping colored circles.
 * Similar to shadcn avatar stacking pattern with negative margin.
 */
function StackedIndicator({ severities, className }: MultiSeverityIndicatorProps) {
    const { t } = useTranslation('re-inspections');
    const sortedSeverities = sortBySeverityPriority(severities);

    // If only one severity, show single indicator
    if (sortedSeverities.length === 1) {
        return (
            <SingleIndicator
                color={sortedSeverities[0].hexColor}
                className={className}
            />
        );
    }

    const tooltipContent = sortedSeverities.map(severity => (
        <div key={severity.inspectionKind} className="flex items-center gap-2">
            <div
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: severity.hexColor }}
            />
            <span>
                {t(`inspectionKinds.${severity.inspectionKind}`, severity.inspectionKind)}
                :
                {' '}
                {t(`classifications.${severity.codeKind}`, severity.codeKind)}
            </span>
        </div>
    ));

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div className={cn('flex -space-x-1.5 cursor-pointer', className)}>
                    {sortedSeverities.map(severity => (
                        <div
                            key={severity.inspectionKind}
                            className="h-4 w-4 rounded-full ring-2 ring-background"
                            style={{ backgroundColor: severity.hexColor }}
                        />
                    ))}
                </div>
            </TooltipTrigger>
            <TooltipContent>
                <div className="flex flex-col gap-1">
                    {tooltipContent}
                </div>
            </TooltipContent>
        </Tooltip>
    );
}

/**
 * Dots with scope labels (e.g., "S8", "S10").
 * Shows compact labels next to colored indicators.
 */
export function LabeledIndicator({ severities, className }: MultiSeverityIndicatorProps) {
    const { t } = useTranslation('elektra');

    const sortedSeverities = useMemo<ScopeSeverity[]>(() => sortBySeverityPriority(severities), [severities]);

    function getLabel(inspectionKind: InspectionKinds): string {
        return t(`electricalInspectionKind.${inspectionKind}`, inspectionKind);
    };

    return (
        <div className={cn('flex items-center gap-2', className)}>
            {sortedSeverities.map(severity => (
                <div key={severity.inspectionKind} className="flex items-center gap-1">
                    <div
                        className="h-3 w-3 rounded-full text-nowrap whitespace-nowrap"
                        style={{ backgroundColor: severity.hexColor }}
                    />
                    <span className="text-sm font-medium text-text-secondary">
                        {getLabel(severity.inspectionKind)}
                    </span>
                </div>
            ))}
        </div>
    );
}
